import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Rating,
} from '@mui/material';
import {
  ZAvatar,
  OutlinedIcons,
  ContextMenuComplete,
  RichTextDisplay,
  decodeHTMLEntities,
  replaceNewLine,
  getFormattedDateShortMonthDDYYYY,
  mapFindZicassoToString,
  convertRatingPercentageToNumber,
} from '@marageti/z4-lib';
import { Review } from '@marageti/z4-sdk/lib/travel';
import useDestinations from '../../hooks/use-destinations';

export type ReviewProps = {
  review: Partial<Review>,
  onClickEdit?: () => void;
};

const getFullName = (firstName: string, lastName: string) => [firstName, lastName].filter(Boolean).join(' ');

const ReviewComponent = ({
  review,
  onClickEdit = undefined,
}: ReviewProps) => {
  const {
    title,
    rating,
    mainReview,
    travelerName,
    travelerAvatar,
    createdAt,
    bestParts,
    doDifferently,
    findZicasso,
    agentReply,
    zicassoReply,
    nextDestinations,
  } = review;

  const { destinationsList } = useDestinations(nextDestinations);
  const whereNext = destinationsList.map((dest) => dest?.name).join(', ');

  const contentItems = [
    {
      title: 'What were the most enjoyable or memorable parts of your trip?',
      content: bestParts,
    },
    {
      title: 'If you could take your trip again, what would you do differently?',
      content: doDifferently,
    },
    {
      title: 'Where are you thinking of traveling next?',
      content: whereNext,
    },
    {
      title: 'How did you learn about Zicasso?',
      content: mapFindZicassoToString(findZicasso),
    },
  ].filter(Boolean).filter((item) => !!item && !!item.content) as { title: string, content: string }[];

  const reviewTitle = title && replaceNewLine(decodeHTMLEntities(title));
  const reviewSubtitle = (travelerName && createdAt) && `${travelerName} · ${getFormattedDateShortMonthDDYYYY(createdAt) || ''}`;
  const content = mainReview && replaceNewLine(decodeHTMLEntities(mainReview));
  const agentName = agentReply && getFullName(agentReply.person.givenName, agentReply.person.familyName);
  const zicassoName = zicassoReply && getFullName(zicassoReply.person.givenName, zicassoReply.person.familyName);
  const ratingPercentage = rating && convertRatingPercentageToNumber(rating);

  const replies = [
    { from: agentName || 'Travel Specialist', content: agentReply?.reply && replaceNewLine(decodeHTMLEntities(agentReply.reply)) },
    { from: zicassoName || 'Zicasso', content: zicassoReply?.reply && replaceNewLine(decodeHTMLEntities(zicassoReply.reply)) },
  ].filter(Boolean).filter((item) => !!item.content);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Rating value={ratingPercentage} size="small" readOnly />
          {onClickEdit && (
          <ContextMenuComplete
            id="edit-review"
            items={[{ action: onClickEdit, label: 'Edit' }]}
          />
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          {reviewTitle && (
          <Typography
            component="div"
            fontWeight="bold"
            textAlign="left"
            variant="h2"
            dangerouslySetInnerHTML={{ __html: reviewTitle }}
          />
          )}
        </Stack>
        {reviewSubtitle && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <ZAvatar size={24} src={travelerAvatar} name={travelerName} />
            <Typography
              component="div"
              dangerouslySetInnerHTML={{ __html: reviewSubtitle }}
              variant="caption"
            />
          </Stack>
        )}
      </Stack>

      {content && (
        <RichTextDisplay color="text.secondary">
          {content}
        </RichTextDisplay>
      )}

      {/* Render Content Items */}
      {contentItems && contentItems.length > 0 && contentItems.map((contentItem, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={index}>
          {contentItem.title && (
            <Typography
              component="div"
              variant="bodyBold"
              sx={{ mb: 1 }}
              dangerouslySetInnerHTML={{ __html: contentItem.title }}
            />
          )}
          {contentItem.content && (
            <RichTextDisplay color="text.secondary">
              {contentItem.content}
            </RichTextDisplay>
          )}
        </Box>
      ))}

      {/* Render Replies */}
      {replies && replies.length > 0 && replies.map((reply, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Card key={index} variant="dense">
          <CardHeader
            disableTypography
            title={(
              <Stack direction="row" spacing={1.25} alignItems="center">
                <OutlinedIcons.OutlinedReplyIcon color="primary" fontSize="small" />
                <Typography
                  component="div"
                  variant="bodyBold"
                  dangerouslySetInnerHTML={{ __html: `Reply from ${reply.from}` }}
                />
              </Stack>
            )}
          />
          {reply.content && (
            <CardContent>
              <RichTextDisplay color="text.secondary">
                {reply.content}
              </RichTextDisplay>
            </CardContent>
          )}
        </Card>
      ))}
    </Stack>
  );
};

export default ReviewComponent;
